import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLinkIcon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EXPENSE_STATE_LABELS, expenseStateVariant } from "@/components/ExpensesTable";
import { ExpenseAuditLogTable } from "@/components/ExpenseAuditLogTable";
import { formatCurrency, formatDateTime } from "@/lib/format";

type Expense = components["schemas"]["ExpenseResponse"];

export function ExpensePage() {
  const { expenseId } = useParams<{ expenseId: string }>();

  const [expense, setExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!expenseId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function fetchExpense() {
      setLoading(true);
      setError(null);
      const { data, error: apiError, response } = await api.GET("/admin/expenses/{id}", {
        params: { path: { id: expenseId! } },
      });
      if (cancelled) return;
      // A 404 is an answer (no such expense), not a failure; anything else is.
      if (apiError && response?.status !== 404) {
        setError("Failed to load expense.");
      } else {
        setExpense(data ?? null);
      }
      setLoading(false);
    }

    fetchExpense();
    return () => {
      cancelled = true;
    };
  }, [expenseId]);

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  const transaction = expense?.cardTransaction;

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Expense</h1>
        {expense?.accountingUrl && (
          <Button asChild variant="outline">
            <a href={expense.accountingUrl} target="_blank" rel="noreferrer">
              <ExternalLinkIcon className="size-4" />
              Open in {expense.accountingPlatform ?? "accounting"}
            </a>
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      ) : expense ? (
        <>
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span>{expense.merchant?.displayName || expense.merchantName || "Unknown merchant"}</span>
                <span className="tabular-nums text-muted-foreground">
                  {formatCurrency(expense.amount, expense.currency)}
                </span>
                {expense.state && (
                  <Badge variant={expenseStateVariant(expense.state)}>{EXPENSE_STATE_LABELS[expense.state]}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Section title="Expense">
                <Field label="Expense ID">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {expense.id}
                  </Badge>
                </Field>
                <Field label="Organisation">
                  <Link to={`/organisations/${expense.organisationId}`}>
                    <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                      {expense.organisationId}
                    </Badge>
                  </Link>
                </Field>
                <Field label="Spender">
                  {expense.spender?.userId ? (
                    <Link to={`/users/${expense.spender.userId}`} className="hover:underline">
                      {expense.spender.name || expense.spender.email}
                    </Link>
                  ) : (
                    <Muted>Not attributed</Muted>
                  )}
                </Field>
                <Field label="Memo">{expense.memo || <Muted>-</Muted>}</Field>
                <Field label="Personal">{expense.isPersonal ? "Yes" : "No"}</Field>
                <Field label="Spent">{formatDateTime(expense.transactionDate)}</Field>
              </Section>

              <Section title="Merchant & card">
                <Field label="Card network printed">
                  <span className="font-mono text-xs">{expense.merchantName || "-"}</span>
                </Field>
                <Field label="Resolved merchant">{expense.merchant?.displayName || <Muted>Unresolved</Muted>}</Field>
                <Field label="Category">
                  {expense.spendCategoryLabel || <Muted>-</Muted>}
                  {expense.mcc && <span className="ml-2 font-mono text-xs text-muted-foreground">MCC {expense.mcc}</span>}
                </Field>
                <Field label="Card">
                  {[expense.cardDisplayName, expense.cardNetwork, expense.cardLast4 && `ending ${expense.cardLast4}`]
                    .filter(Boolean)
                    .join(" · ") || <Muted>-</Muted>}
                </Field>
                <Field label="Transaction status">{transaction?.status ?? <Muted>-</Muted>}</Field>
                <Field label="Settled">
                  {transaction ? (
                    <>
                      {formatCurrency(transaction.transactionSettledAmount ?? undefined, transaction.transactionCurrency)}
                      {transaction.billingCurrency !== transaction.transactionCurrency && (
                        <span className="ml-2 text-muted-foreground">
                          billed {formatCurrency(transaction.billingSettledAmount ?? undefined, transaction.billingCurrency)}
                        </span>
                      )}
                    </>
                  ) : (
                    <Muted>-</Muted>
                  )}
                </Field>
              </Section>

              <Section title="Receipt">
                <Field label="Receipt">
                  {expense.primaryReceiptId ? (
                    <Badge variant="secondary" className="font-mono text-xs">
                      {expense.primaryReceiptId}
                    </Badge>
                  ) : (
                    <Badge variant="outline">Missing</Badge>
                  )}
                </Field>
                <Field label="Matched by">{expense.primaryReceiptId ? expense.matchSource : <Muted>-</Muted>}</Field>
                <Field label="Match score">
                  {expense.matchScore != null ? Number(expense.matchScore).toFixed(2) : <Muted>-</Muted>}
                </Field>
              </Section>

              <Section title="Coding">
                <Field label="Source">{expense.codingSource}</Field>
                <Field label="Confirmed">{expense.codingConfirmed ? "Yes" : "No"}</Field>
                <Field label="Learned account">
                  {expense.learnedCoding ? (
                    <>
                      {expense.learnedCoding.ledgerAccountName}
                      {expense.learnedCoding.voteShare != null && (
                        <span className="ml-2 text-muted-foreground">
                          {Math.round(Number(expense.learnedCoding.voteShare) * 100)}% of similar spend
                        </span>
                      )}
                    </>
                  ) : (
                    <Muted>-</Muted>
                  )}
                </Field>
              </Section>

              {(expense.flagReason || expense.appliedApprovalPolicy) && (
                <Section title="Review">
                  <Field label="Flag">
                    {expense.flagReason ? <Badge variant="destructive">{expense.flagReason}</Badge> : <Muted>-</Muted>}
                  </Field>
                  <Field label="Flagged at">{expense.flaggedAt ? formatDateTime(expense.flaggedAt) : <Muted>-</Muted>}</Field>
                  <Field label="Flag detail">{expense.flagDetail || <Muted>-</Muted>}</Field>
                  <Field label="Approval policy">
                    {expense.appliedApprovalPolicy ? (
                      `${expense.appliedApprovalPolicy.name} (v${expense.appliedApprovalPolicy.version})`
                    ) : (
                      <Muted>-</Muted>
                    )}
                  </Field>
                  <Field label="Approval reasoning" className="sm:col-span-2">
                    {expense.approvalReasoning || <Muted>-</Muted>}
                  </Field>
                </Section>
              )}

              <Section title="Accounting">
                <Field label="Posted at">{expense.postedAt ? formatDateTime(expense.postedAt) : <Muted>Not posted</Muted>}</Field>
                <Field label="Platform">{expense.accountingPlatform || <Muted>-</Muted>}</Field>
                <Field label="Reference">
                  {expense.accountingReference ? (
                    <span className="font-mono text-xs">{expense.accountingReference}</span>
                  ) : (
                    <Muted>-</Muted>
                  )}
                </Field>
                <Field label="Paid from">{expense.paidFromAccountName || <Muted>-</Muted>}</Field>
                <Field label="Apideck ID">
                  {expense.apideckExpenseId ? (
                    <span className="font-mono text-xs">{expense.apideckExpenseId}</span>
                  ) : (
                    <Muted>-</Muted>
                  )}
                </Field>
                <Field label="Post failure">
                  {expense.postFailureReason ? (
                    <span className="text-destructive">{expense.postFailureReason}</span>
                  ) : (
                    <Muted>-</Muted>
                  )}
                </Field>
              </Section>

              <Section title="Audit">
                <Field label="Created">{formatDateTime(expense.createdAt)}</Field>
                <Field label="Updated">{formatDateTime(expense.updatedAt)}</Field>
              </Section>
            </CardContent>
          </Card>

          <h2 className="mb-4 text-lg font-semibold">Lines</h2>
          <div className="mb-8 rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Ledger account</TableHead>
                  <TableHead>Tax</TableHead>
                  <TableHead className="text-right">Tax amount</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>VAT reclaim</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(expense.lines ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-muted-foreground h-16 text-center">
                      Not coded yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  expense.lines!.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell className="tabular-nums text-muted-foreground">{line.lineNumber}</TableCell>
                      <TableCell>{line.description || <Muted>-</Muted>}</TableCell>
                      <TableCell>
                        {line.ledgerAccountId ? (
                          <span className="font-mono text-xs">{line.ledgerAccountId.slice(0, 8)}...</span>
                        ) : (
                          <Badge variant="outline">Uncoded</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {line.taxRateName || line.taxCode || <Muted>-</Muted>}
                        {line.taxRateIssue && <span className="ml-2 text-destructive">{line.taxRateIssue}</span>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {line.taxAmount != null ? formatCurrency(line.taxAmount, expense.currency) : <Muted>-</Muted>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(line.amount, expense.currency)}
                      </TableCell>
                      <TableCell>
                        {line.isVatReclaimable ? (
                          <Badge variant="secondary">Reclaimable</Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">{line.vatReclaimBlockedReason ?? "-"}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{line.source}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <h2 className="mb-4 text-lg font-semibold">History</h2>
          <ExpenseAuditLogTable expenseId={expense.id!} />
        </>
      ) : (
        <p className="text-muted-foreground">Expense not found.</p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">{children}</dl>
    </div>
  );
}

function Field({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}
